import { useQuery } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import MetricCard from '../components/MetricCard';
import { adminApi, mockData } from '../api/client';

export default function Dashboard() {
  const { data: metrics, isLoading: metricsLoading } = useQuery({
    queryKey: ['dashboardStats'],
    queryFn: () => adminApi.getDashboardStats('30d'),
    placeholderData: mockData.dashboardMetrics(),
  });

  const { data: revenueData, isLoading: revenueLoading } = useQuery({
    queryKey: ['revenueData'],
    queryFn: () => adminApi.getRevenueData(30),
    placeholderData: mockData.revenueData(),
  });

  const { data: recentSignups, isLoading: signupsLoading } = useQuery({
    queryKey: ['recentSignups'],
    queryFn: () => adminApi.getRecentSignups(5),
    placeholderData: mockData.recentSignups(),
  });

  const churnAlerts = mockData.churnAlerts(); // Keep mock for now - needs separate API

  const isLoading = metricsLoading || revenueLoading || signupsLoading;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTierBadgeColor = (tier: string) => {
    switch (tier) {
      case 'enterprise':
        return 'bg-purple-500/20 text-purple-400';
      case 'pro':
        return 'bg-blue-500/20 text-blue-400';
      default:
        return 'bg-slate-600 text-slate-300';
    }
  };

  const getRiskBadgeColor = (score: number) => {
    if (score >= 0.8) return 'bg-red-500/20 text-red-400';
    if (score >= 0.6) return 'bg-yellow-500/20 text-yellow-400';
    return 'bg-green-500/20 text-green-400';
  };

  if (isLoading || !metrics || !revenueData || !recentSignups) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-pulse text-slate-400">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-slate-400 mt-1">Overview of your portal metrics</p>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Monthly Recurring Revenue"
          value={formatCurrency(metrics.mrr)}
          change={metrics.mrrChange}
          trend="up"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
        <MetricCard
          title="Active Users"
          value={formatNumber(metrics.activeUsers)}
          change={metrics.activeUsersChange}
          trend="up"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          }
        />
        <MetricCard
          title="Credits Consumed"
          value={formatNumber(metrics.creditsConsumed)}
          change={metrics.creditsConsumedChange}
          trend="up"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          }
        />
        <MetricCard
          title="Churn Rate"
          value={`${metrics.churnRate}%`}
          change={metrics.churnRateChange}
          trend="down"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
            </svg>
          }
        />
      </div>

      {/* Revenue Chart */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4">Revenue Over Time</h2>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis
                dataKey="date"
                tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                stroke="#64748b"
                fontSize={12}
              />
              <YAxis
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                stroke="#64748b"
                fontSize={12}
              />
              <Tooltip
                formatter={(value: number) => [formatCurrency(value), 'Revenue']}
                labelFormatter={(label) => new Date(label).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                contentStyle={{ borderRadius: '8px', border: '1px solid #334155', backgroundColor: '#1e293b', color: '#f1f5f9' }}
              />
              <Line
                type="monotone"
                dataKey="revenue"
                stroke="#60a5fa"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 6, fill: '#60a5fa' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bottom Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Signups */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Recent Signups</h2>
            <a href="/users" className="text-sm text-primary-400 hover:text-primary-300 font-medium">
              View all
            </a>
          </div>
          <div className="space-y-4">
            {recentSignups.map((signup: { id: string; email: string; tier: string; createdAt: string }) => (
              <div key={signup.id} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-slate-300">
                      {signup.email.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{signup.email}</p>
                    <p className="text-xs text-slate-400">{formatDate(signup.createdAt)}</p>
                  </div>
                </div>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getTierBadgeColor(signup.tier)}`}>
                  {signup.tier}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Churn Risk Alerts */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Churn Risk Alerts</h2>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
              {churnAlerts.length} alerts
            </span>
          </div>
          <div className="space-y-4">
            {churnAlerts.map((alert) => (
              <div key={alert.id} className="p-4 bg-slate-700/50 rounded-lg">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-white">{alert.email}</p>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getRiskBadgeColor(alert.riskScore)}`}>
                        {Math.round(alert.riskScore * 100)}% risk
                      </span>
                    </div>
                    <p className="text-sm text-slate-400 mt-1">{alert.reason}</p>
                  </div>
                  <button className="btn-secondary text-xs px-3 py-1.5">
                    Take Action
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
